import React from 'react';
import { Layout } from 'antd';
import { useTheme } from '../Shared/GlobalContext/ThemeContext';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import useSocketSubscription from '../../Hooks/useSocketSubscription';
import { Resource } from '../../Constants/socket';
import useCredentialFetching from './hooks/useCredentialFetching';
import useCredentialActions from './hooks/useCredentialActions';
import useCredentialSocketEvents from './hooks/useCredentialSocketEvents';
import useCredentialModals from './hooks/useCredentialModals';
import CredentialTable from './CredentialTable/CredentialTable';
import AddCredentialModal from './AddCredentialModal/AddCredentialModal';
import CredentialDetailsModal from './CredentialDetailsModal/CredentialDetailsModal';
import ResourcesModal from './ResourcesModal/ResourcesModal';


const Credentials = () => {
    const { isDarkMode } = useTheme();
    const { handleError } = useErrorHandler();
    const { user: { id: currentUserId } = {} } = useGlobal();

    // Subscribe to user-scoped channel for real-time updates
    useSocketSubscription(Resource.USER, currentUserId ? [currentUserId] : null, true);

    // Custom Hooks
    const {
        credentials,
        setCredentials,
        loading,
        isInitialLoading,
        pagination,
        searchString,
        filters,
        sorter,
        refetchCredentials,
        handleTableChange,
        handleSearch,
        handleFilterChange,
    } = useCredentialFetching(handleError);

    const {
        handleCreate,
        handleUpdate,
        handleDelete,
        createLoading,
        updateLoading,
        deleteLoading,
    } = useCredentialActions(refetchCredentials, handleError);

    const {
        getHighlightClass,
        softRefreshKey,
    } = useCredentialSocketEvents(credentials, setCredentials);

    const {
        showAddModal,
        showEditModal,
        showDetailsModal,
        showResourcesModal,
        selectedCredential,
        handleShowAdd,
        handleShowEdit,
        handleShowDetails,
        handleShowResources,
        handleCloseModals,
    } = useCredentialModals();

    const handleAction = (action, record) => {
        switch (action) {
            case 'view':
                handleShowDetails(record);
                break;
            case 'edit':
                handleShowEdit(record);
                break;
            case 'delete':
                handleDelete(record.id);
                break;
            case 'resources':
                handleShowResources(record);
                break;
            default:
                break;
        }
    };

    return (
        <Layout theme={isDarkMode ? 'dark' : 'light'}>
            <CredentialTable
                credentials={credentials}
                loading={loading}
                isInitialLoading={isInitialLoading}
                pagination={pagination}
                searchString={searchString}
                filters={filters}
                sorter={sorter}
                onTableChange={handleTableChange}
                onSearch={handleSearch}
                onFilterChange={handleFilterChange}
                onAction={handleAction}
                onAdd={handleShowAdd}
                getHighlightClass={getHighlightClass}
                softRefreshKey={softRefreshKey}
                deleteLoading={deleteLoading}
            />

            {showAddModal && (
                <AddCredentialModal
                    onCancel={handleCloseModals}
                    onCreate={handleCreate}
                    loading={createLoading}
                />
            )}

            {showEditModal && selectedCredential && (
                <AddCredentialModal
                    onCancel={handleCloseModals}
                    onUpdate={handleUpdate}
                    isEdit={true}
                    credential={selectedCredential}
                    loading={updateLoading}
                />
            )}

            {showDetailsModal && selectedCredential && (
                <CredentialDetailsModal
                    credential={selectedCredential}
                    onCancel={handleCloseModals}
                />
            )}

            {showResourcesModal && selectedCredential && (
                <ResourcesModal
                    credential={selectedCredential}
                    onCancel={handleCloseModals}
                />
            )}
        </Layout>
    );
};

export default Credentials;
